import { IMicrositeCampaign } from "@nkr/models/microsites";
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import MontserratRegular from 'components/assets/fonts/Montserrat-Regular.ttf';
import MontserratBold from 'components/assets/fonts/Montserrat-Bold.ttf';
import MontserratItalic from 'components/assets/fonts/Montserrat-Italic.ttf'
import { renderToStaticMarkup } from "react-dom/server";
import QRCode from "react-qr-code";
import { useEffect, useState } from "react";

interface IProps {
    campaign: IMicrositeCampaign
    data: any[],
}

Font.register({
    family: 'Montserrat',
    fonts: [
        { src: MontserratRegular }, // Regular
        { src: MontserratBold, fontWeight: 'bold' }, // Bold
        { src: MontserratItalic, fontStyle: 'italic' }, // Italic
        // Add other font styles as needed (e.g., italic)
    ],
});

const styles = StyleSheet.create({
    body: {
        fontFamily: 'Montserrat',
    },
    header: {
        flexDirection: 'row',
    },
    logoContainer: {
        margin: 10,
        paddingLeft: 5,
        flexGrow: 1
    },
    logo: {
        width: '228', // Adjust width as needed
        height: 'auto', // Maintain aspect ratio
    },
    nkrLink: {
        margin: 10,
        padding: 10,
        flexGrow: 1,
        width: '100%',
        textAlign: 'right',
        color: '#c8102e',
        fontSize: 10
    },
    address: {
        marginTop:400,
        marginLeft:100,
        fontSize:10
    },
    mainBody: {
        marginTop:10,
        marginLeft:50,
        marginRight:50,
        fontSize:10
    },
    columns: {
        marginTop:20,
        flexDirection: 'row',
    },
    column: {
        marginLeft:50,
        flexGrow: 1,
        fontSize:10,
        width:170
    },
    imageColumn: {
        flexGrow: 1,
        marginLeft: 20
    },
    previewImage: {
        width: '260', // Adjust width as needed
        height: 'auto', // Maintain aspect ratio
    },
    sigImage: {
        width: 200, 
        height: 'auto', 
        marginTop: 20, 
        marginBottom:20 
    },
    webURL: {
        color: '#c8102e',
    },
    campaignCode: {
        fontWeight: 'bold'
    }
});

const svgToPngDataURL = (svgMarkup: string, size: number): Promise<string> => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        const img = new (window as any).Image(); // Using any to bypass potential type issues with global Image object
        img.onload = () => {
            ctx?.drawImage(img, 0, 0, size, size);
            const pngDataUrl = canvas.toDataURL('image/png');
            resolve(pngDataUrl); 
        };
        img.onerror = (error: Event | string) => {
            console.error("Error loading SVG for PNG conversion:", error); 
            reject(error); //
        };
        img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgMarkup)))}`;
    });
};

export const generateQRCodePngDataURL = async (value: string, size: number = 128): Promise<string> => {
    const svgMarkup: string = renderToStaticMarkup(
        <QRCode value={value} size={size} level="H" />
    );

    try {
        const pngDataUrl = await svgToPngDataURL(svgMarkup, size);
        return pngDataUrl;
    } catch (error) {
        console.error("Failed to generate PNG QR code:", error);
        return '';
    }
};

const replaceKeys = (str:string, campaign:IMicrositeCampaign) => {
    return str
        .replace('[CENTER-ALIAS]', campaign.centerAlias)
        .replace('[CENTER-NAME]', campaign.centerName);
}

const insertCampaignCode = (str:string, campaign:IMicrositeCampaign) => {
    let ret = [];
    const arr = str.split('[CAMPAIGN-CODE]');
    arr.forEach((s,i) => {
        ret.push(s);
        if(i != (arr.length - 1)) {
            ret.push(<Text style={styles.campaignCode}>{campaign.selfRegistrationKey}</Text>);
        }
    })
    return (<Text>{ret}</Text>)
}

const insertRegistrationLink = (str:string, campaign:IMicrositeCampaign) => {
    let ret = [];
    const arr = str.split('[REGISTRATION-LINK]');
    arr.forEach((s,i) => {
        ret.push(insertCampaignCode(s,campaign));
        if(i != (arr.length - 1)) {
            ret.push(<Text style={styles.webURL}>www.nkr.org/register</Text>);
        }
    })
    return (<Text>{ret}</Text>)
}

const CampaignDocument = ({campaign, data}:IProps) => {
    const [qrCodePngImage, setQrCodePngImage] = useState<string | null>(null); 

    useEffect(() => {
        const generateAndSetPngQr = async () => {
            const imageUrl = await generateQRCodePngDataURL(`https://nkr.org/register/${campaign?.selfRegistrationKey}`); 
            setQrCodePngImage(imageUrl); //
        };
        
        generateAndSetPngQr();
    }, [campaign?.selfRegistrationKey]);

    return (
        <Document>
            {data.filter((f,i) => i < 1).map((r, index) => (<>
                {/* <Page size="A4" key={index}>
                    <View style={styles.address}>
                        <Text>{r.firstName?.toUpperCase()} {r.lastName?.toUpperCase()}</Text>
                        <Text>{r.addressLine1?.toUpperCase()}</Text>
                        <Text>{r.addressLine2?.toUpperCase()}</Text>
                        <Text>{r.city?.toUpperCase()} {r.state?.toUpperCase()}, {r.postalCode}</Text>
                    </View>
                </Page> */}
                <Page size="A4" key={`${index}b`} style={styles.body}>
                    <View style={styles.header}>
                        <View style={styles.logoContainer}>
                            <Image src={'./images/logo2.png'} style={styles.logo} />
                        </View>
                        <View style={styles.nkrLink}>
                            <Text>www.kidneyregistry.com</Text>
                        </View>
                    </View>
                    <View style={styles.mainBody}>
                        <Text>{replaceKeys(campaign.letter.salutation, campaign)}</Text>
                        <Text>{' '}</Text>
                        {insertRegistrationLink(replaceKeys(campaign.letter.intro, campaign), campaign)}
                        
                    </View>
                    <View style={styles.columns}>
                        <View style={styles.column}>
                            {insertRegistrationLink(replaceKeys(campaign.letter.closing, campaign), campaign)}
                            <Image style={{ width: 100, height: 'auto', marginTop: 20, marginBottom:20 }} src={qrCodePngImage} />
                            <Image style={styles.sigImage} src={`${__API__}/microsite/campaign/${campaign?.id}/signature-file`} />
                            <Text>{campaign.letter.signature}</Text>
                        </View>
                        <View style={styles.imageColumn}>
                            <Image src={'/images/microsite-preview-5.png'} style={styles.previewImage} />
                        </View>
                    </View>
                </Page>
            </>))}
        </Document>
    )};
export default CampaignDocument;